import moment from "moment";

// Seeding times for rows the closer adds or drags into the Closer grid.
//
// Trip validation orders events by Time alone, so two events on the same timestamp
// come back in an undefined order and can trip the sequence checks at random. A row
// the closer adds or moves therefore has to land strictly between the rows it sits
// between. The gap mirrors OrderApi's MinimumEventGap (1s) used when it places
// suggested events.
//
// Only the rows being placed get a time from here. Neighbouring rows are never moved:
// changing a time the closer did not touch would be a silent edit to the trip.

export const MIN_EVENT_GAP_MS = 1000;

export interface SeededTimes {
  // One ISO timestamp per placed row, in grid order. Null only when neither
  // neighbour has a time to count from.
  times: (string | null)[];
  // False when the neighbours are too close together to keep MIN_EVENT_GAP_MS on
  // every side. The times are still distinct, but the caller should warn the closer.
  fits: boolean;
}

const toMs = (time: string | null | undefined): number | null => {
  if (!time) return null;
  const ms = moment.utc(time).valueOf();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * Times for `count` rows placed between `aboveTime` and `belowTime` (either may be
 * missing). The rows sit `offsetMs` before the row below — or after the row above
 * when there is no row below — and MIN_EVENT_GAP_MS apart from each other, then are
 * pulled back inside the neighbours if that overshoots the row above.
 */
export const seedTimesBetween = (
  aboveTime: string | null | undefined,
  belowTime: string | null | undefined,
  count: number,
  offsetMs: number
): SeededTimes => {
  const above = toMs(aboveTime);
  const below = toMs(belowTime);
  const toIso = (ms: number[]) => ms.map((m) => moment.utc(m).toISOString());

  if (above === null && below === null) return { times: Array(count).fill(null), fits: true };

  let times: number[];
  if (below !== null) {
    const last = below - Math.max(offsetMs, MIN_EVENT_GAP_MS);
    times = Array.from({ length: count }, (_, i) => last - (count - 1 - i) * MIN_EVENT_GAP_MS);
  } else {
    const first = (above as number) + Math.max(offsetMs, MIN_EVENT_GAP_MS);
    times = Array.from({ length: count }, (_, i) => first + i * MIN_EVENT_GAP_MS);
  }

  if (above === null || below === null || times[0] - above >= MIN_EVENT_GAP_MS) {
    return { times: toIso(times), fits: true };
  }

  const span = below - above;
  if (span >= (count + 1) * MIN_EVENT_GAP_MS) {
    // Room for the full gap everywhere: slide the rows down to just after the row above.
    return { times: toIso(times.map((_, i) => above + (i + 1) * MIN_EVENT_GAP_MS)), fits: true };
  }

  if (span > count) {
    // Not enough room for the full gap: share what there is evenly, still strictly
    // between the neighbours and distinct from each other.
    return {
      times: toIso(times.map((_, i) => above + Math.floor(((i + 1) * span) / (count + 1)))),
      fits: false,
    };
  }

  // The neighbours are tied or out of order themselves, so nothing fits between them.
  // Keep the times counted back from the row below: they are distinct from each other
  // and from both neighbours, and the closer is warned to fix the order.
  return { times: toIso(times), fits: false };
};

export const SPACING_WARNING =
  "No room between the neighbouring events for a distinct time. Check the times on this row and the rows around it.";
